import { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, List, GitBranch, Clock, Users } from 'lucide-react';
import ListToolbar, { PohledPill, PohledTlacitko } from '../../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../../layout/ListFiltr';
import Avatar from '../Avatar';
import OrgStrom from './OrgStrom';
import {
    SECTION_COLOR, OK, STAV_LABEL, datum, mesicRok, prijmeniJmeno,
    stavCertifikatu, pozadovaneCertifikaty, chybiCertifikat, expirujeBrzy, vNastupu, onboarding,
} from '../lide';

/**
 * Lidé → Organizace: seznam podle normy (`standard-rozvrzeni.md` §2, §3).
 *
 * **Skupiny podle pozice** se souhrnem ve stejných sloupcích jako lidé —
 * certifikáty „3/4" (kolik z těch, kdo je potřebují, je má platné), kolik jich
 * nastupuje. Pozice, ne oddělení: oddělení má člověk víc naráz, pozici jednu.
 *
 * **Certifikáty jako sloupce s datem platnosti** (měsíc/rok). Červeně jen
 * *chybí* a *expiroval*; do 30 dní tučně s počtem dní — třetí barva by se od
 * červené nedala odlišit (norma §3). Prázdná buňka = pozice ho nevyžaduje.
 *
 * Dvě hustoty, jedno rozmístění: přehled = mřížka přes celou šířku, vedle
 * karty = úzké dvouřádkové řádky. Strom je druhý pohled na tatáž data.
 */

export const POHLEDY = [
    { id: 'aktivni', label: 'Aktivní', test: () => true },
    { id: 'nastup', label: 'V nástupu', test: (z) => vNastupu(z) },
    { id: 'chybi', label: 'Chybí certifikát', test: (z) => chybiCertifikat(z), upozorneni: true },
    { id: 'expiruje', label: 'Expiruje do 30 dní', test: (z) => expirujeBrzy(z) },
    { id: 'nepodepsano', label: 'Nepodepsáno', test: (z, k) => (k.nepodepsane[z.id] || 0) > 0, upozorneni: true, jenDokumenty: true },
    { id: 'archiv', label: 'Archiv', test: () => true },
];

// Odebírání sloupců na užších oknech v pevném pořadí (norma §3)
const ODEBIRANI = [
    { od: 1240, sloupec: 'nadrizeny' },
    { od: 1140, sloupec: 'oddeleni' },
    { od: 1040, sloupec: 'nastup' },
    { od: 940, sloupec: 'onboarding' },
];

function useSirka(ref) {
    const [sirka, setSirka] = useState(1600);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(([e]) => setSirka(e.contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return sirka;
}

export default function LideListPanel({
    siroky = false, kompaktni = false, isDark, mutedColor, inputStyle,
    zamestnanci = [], archiv = null, loading = false,
    hledani, setHledani, pohled, setPohled, filtr, setFiltr,
    zobrazeni, setZobrazeni,
    certTypy = [], onboardingMap = {}, nepodepsane = {}, vidiDokumenty = false,
    activeId, onSelect, rezervaDole = null,
}) {
    const [sbalene, setSbalene] = useState(() => new Set());
    const plochaRef = useRef(null);
    const sirkaPlochy = useSirka(plochaRef);
    const hranice = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';

    const pohledy = POHLEDY.filter(p => !p.jenDokumenty || vidiDokumenty);
    const aktivniPohled = pohledy.find(p => p.id === pohled) || pohledy[0];
    const kontext = { nepodepsane };
    const zdroj = aktivniPohled.id === 'archiv' ? (archiv || []) : zamestnanci;

    const filtrSkupiny = useMemo(() => {
        const unikatni = (fn) => [...new Map(zamestnanci.flatMap(fn).filter(Boolean).map(x => [x.value, x])).values()]
            .sort((a, b) => a.label.localeCompare(b.label, 'cs'));
        return [
            { id: 'oddeleni', typ: 'dropdown', label: 'Oddělení', options: unikatni(z => (z.oddeleni || []).map(o => ({ value: o.id, label: o.nazev }))) },
            { id: 'uvazek', typ: 'dropdown', label: 'Úvazek', options: unikatni(z => z.typ_uvazku ? [{ value: z.typ_uvazku.id, label: z.typ_uvazku.nazev }] : []) },
            { id: 'nadrizeny', typ: 'dropdown', label: 'Nadřízený', options: unikatni(z => z.nadrizeny ? [{ value: z.nadrizeny.id, label: prijmeniJmeno(z.nadrizeny) }] : []) },
        ];
    }, [zamestnanci]);

    const vyfiltrovani = useMemo(() => {
        const q = hledani.trim().toLowerCase();
        return zdroj.filter(z => {
            if (aktivniPohled.id !== 'archiv' && !aktivniPohled.test(z, kontext)) return false;
            if (filtr.oddeleni && !(z.oddeleni || []).some(o => String(o.id) === String(filtr.oddeleni))) return false;
            if (filtr.uvazek && String(z.typ_uvazku?.id) !== String(filtr.uvazek)) return false;
            if (filtr.nadrizeny && String(z.nadrizeny_id) !== String(filtr.nadrizeny)) return false;
            if (!q) return true;
            return [z.jmeno, z.prijmeni, z.pozice?.nazev, z.telefon, z.email, ...(z.oddeleni || []).map(o => o.nazev)]
                .some(v => (v || '').toString().toLowerCase().includes(q));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zdroj, hledani, aktivniPohled, filtr, nepodepsane]);

    // Skupiny podle pozice, v každé abecedně podle příjmení
    const skupiny = useMemo(() => {
        const mapa = new Map();
        for (const z of vyfiltrovani) {
            const k = z.pozice?.nazev || 'Bez pozice';
            if (!mapa.has(k)) mapa.set(k, []);
            mapa.get(k).push(z);
        }
        return [...mapa.entries()]
            .sort(([a], [b]) => (a === 'Bez pozice') - (b === 'Bez pozice') || a.localeCompare(b, 'cs'))
            .map(([nazev, lide]) => ({ nazev, lide: lide.sort((a, b) => prijmeniJmeno(a).localeCompare(prijmeniJmeno(b), 'cs')) }));
    }, [vyfiltrovani]);

    const pocet = (p) => (p.id === 'archiv' ? (archiv ? archiv.length : null) : zamestnanci.filter(z => p.test(z, kontext)).length);
    const nastavFiltr = (id, v) => setFiltr(f => ({ ...f, [id]: v || '' }));
    const resetFiltru = () => setFiltr({ oddeleni: '', uvazek: '', nadrizeny: '' });
    const prepniSkupinu = (nazev) => setSbalene(s => { const n = new Set(s); n.has(nazev) ? n.delete(nazev) : n.add(nazev); return n; });

    const skryte = new Set(ODEBIRANI.filter(o => sirkaPlochy < o.od).map(o => o.sloupec));
    const sloupce = [
        { id: 'jmeno', label: 'Zaměstnanec', sirka: 'minmax(220px, 3fr)' },
        { id: 'oddeleni', label: 'Oddělení', sirka: 'minmax(120px, 1.3fr)' },
        { id: 'uvazek', label: 'Úvazek', sirka: '60px' },
        { id: 'stav', label: 'Stav', sirka: '108px' },
        { id: 'nadrizeny', label: 'Nadřízený', sirka: 'minmax(120px, 1.1fr)' },
        { id: 'nastup', label: 'Nástup', sirka: '90px' },
        { id: 'onboarding', label: 'Onboarding', sirka: '118px' },
        ...certTypy.map(t => ({ id: `cert-${t.kod}`, label: `${t.odznak ? `${t.odznak} ` : ''}${t.nazev}`, sirka: '96px', cert: t.kod })),
    ].filter(s => !skryte.has(s.id));
    const grid = { display: 'grid', gridTemplateColumns: sloupce.map(s => s.sirka).join(' '), columnGap: 14, alignItems: 'center' };
    const strom = zobrazeni === 'strom' && siroky;

    return (
        <div ref={plochaRef} className="flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni} sklo isDark={isDark}
                mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat jméno, pozici, telefon…', placeholderKratky: 'Hledat' }}
                filtr={(
                    <FiltrTlacitko skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={resetFiltru}
                        pocetVysledku={vyfiltrovani.length} siroky={siroky} kompaktni={kompaktni}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
                // V úzkém sloupci vedle karty by se šest pilulek zalomilo do dvou řad —
                // tam jede ovladač se zkratkami na to, co hlásí problém (norma §2).
                pohledy={siroky || kompaktni ? null : pohledy.map(p => ({
                    id: p.id, label: p.label, pocet: pocet(p),
                    upozorneni: p.upozorneni, barva: p.upozorneni ? '#EF4444' : undefined,
                }))}
                pohledAktivni={aktivniPohled.id}
                onPohled={setPohled}
                pohled={!(siroky || kompaktni) ? null : pohledy.map(p => {
                    const n = pocet(p);
                    return (
                        <PohledPill key={p.id} label={p.label} pocet={n} aktivni={aktivniPohled.id === p.id}
                            upozorneni={p.upozorneni && n > 0} onClick={() => setPohled(p.id)}
                            barva={SECTION_COLOR} isDark={isDark} mutedColor={mutedColor} kompaktni={kompaktni} />
                    );
                })}
                pohledVpravo={siroky ? (
                    <>
                        <PohledTlacitko Icon={List} label="Seznam" aktivni={zobrazeni !== 'strom'} onClick={() => setZobrazeni('seznam')} barva={SECTION_COLOR} mutedColor={mutedColor} />
                        <PohledTlacitko Icon={GitBranch} label="Strom" aktivni={zobrazeni === 'strom'} onClick={() => setZobrazeni('strom')} barva={SECTION_COLOR} mutedColor={mutedColor} />
                    </>
                ) : null}
                aktivniFiltry={(
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={resetFiltru}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} />
                )}
            />

            {strom ? (
                <OrgStrom zamestnanci={vyfiltrovani} onSelect={onSelect} aktivniId={activeId} isDark={isDark} mutedColor={mutedColor} />
            ) : (
                <>
                    {siroky && vyfiltrovani.length > 0 && (
                        <div style={{ ...grid, padding: '10px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', color: mutedColor, flexShrink: 0, textTransform: 'uppercase' }}>
                            {sloupce.map(s => <span key={s.id} className="truncate" title={s.label}>{s.label}</span>)}
                        </div>
                    )}
                    <div className="overflow-y-auto" style={{ flex: 1, minHeight: 0, borderTop: siroky ? `1px solid ${hranice}` : 'none', paddingBottom: rezervaDole || undefined }}>
                        {loading || (aktivniPohled.id === 'archiv' && archiv === null) ? (
                            <p className="text-center text-xs py-8" style={{ color: mutedColor }}>Načítám…</p>
                        ) : vyfiltrovani.length === 0 ? (
                            <div className="text-center px-6 py-10">
                                <Users size={26} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 10px' }} />
                                <p className="text-xs" style={{ color: mutedColor }}>
                                    {hledani.trim() ? `Nic nenalezeno pro „${hledani}"` : 'V tomhle pohledu nikdo není.'}
                                </p>
                            </div>
                        ) : skupiny.map(sk => {
                            const sbalena = sbalene.has(sk.nazev);
                            return (
                                <div key={sk.nazev}>
                                    <SkupinaRadek sk={sk} sbalena={sbalena} onClick={() => prepniSkupinu(sk.nazev)}
                                        siroky={siroky} kompaktni={kompaktni} grid={grid} sloupce={sloupce}
                                        onboardingMap={onboardingMap} isDark={isDark} mutedColor={mutedColor} hranice={hranice} />
                                    {!sbalena && sk.lide.map(z => (
                                        siroky
                                            ? <RadekMrizka key={z.id} z={z} grid={grid} sloupce={sloupce} aktivni={activeId === z.id}
                                                onSelect={onSelect} checklist={onboardingMap[z.id]} isDark={isDark} mutedColor={mutedColor} hranice={hranice} />
                                            : <RadekUzky key={z.id} z={z} aktivni={activeId === z.id} onSelect={onSelect}
                                                certTypy={certTypy} checklist={onboardingMap[z.id]} nepodepsano={nepodepsane[z.id] || 0}
                                                isDark={isDark} mutedColor={mutedColor} hranice={hranice} kompaktni={kompaktni} />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {!loading && vyfiltrovani.length > 0 && !kompaktni && (
                <div className="px-3 py-2 text-[10.5px]" style={{ borderTop: `1px solid ${hranice}`, color: mutedColor, flexShrink: 0 }}>
                    {vyfiltrovani.length} {vyfiltrovani.length === 1 ? 'člověk' : vyfiltrovani.length < 5 ? 'lidé' : 'lidí'} · {skupiny.length} {skupiny.length === 1 ? 'pozice' : skupiny.length < 5 ? 'pozice' : 'pozic'}
                    {siroky && certTypy.length > 0 && ' · certifikát: platí do (měsíc/rok), prázdné = pozice ho nevyžaduje'}
                </div>
            )}
        </div>
    );
}

/** Buňka certifikátu v mřížce. */
function CertBunka({ z, kod, mutedColor }) {
    const s = stavCertifikatu(z, kod);
    if (s.stav === 'nevyzaduje') return <span />;
    if (s.stav === 'chybi') return <span style={{ color: '#f87171', fontWeight: 600 }}>chybí</span>;
    if (s.stav === 'expirovano') return <span style={{ color: '#f87171', fontWeight: 600 }} title={`expiroval ${datum(s.cert.platnost_do)}`}>expiroval</span>;
    if (s.stav === 'brzy') {
        return (
            <span className="inline-flex items-center" style={{ gap: 4, color: 'var(--text-primary)', fontWeight: 700 }} title={`platí do ${datum(s.cert.platnost_do)}`}>
                <Clock size={12} /> za {s.dni} d
            </span>
        );
    }
    return <span className="tabular-nums" style={{ color: mutedColor }} title={`platí do ${datum(s.cert.platnost_do)}`}>do {mesicRok(s.cert.platnost_do)}</span>;
}

function OnboardingBunka({ z, checklist, mutedColor, isDark }) {
    const ob = onboarding(checklist);
    if (!ob || !vNastupu(z)) return <span style={{ color: mutedColor }}>{ob ? 'hotovo' : '—'}</span>;
    return (
        <span className="flex items-center" style={{ gap: 8 }} title={`Nástup: ${ob.hotovo}/${ob.celkem} kroků`}>
            <span style={{ flex: 1, height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
                <span style={{ display: 'block', height: '100%', borderRadius: 2, width: `${(ob.hotovo / ob.celkem) * 100}%`, background: SECTION_COLOR }} />
            </span>
            <span className="tabular-nums" style={{ fontSize: 12, color: 'var(--text-primary)' }}>{ob.hotovo}/{ob.celkem}</span>
        </span>
    );
}

function Stav({ z, mutedColor }) {
    return (
        <span className="flex items-center" style={{ gap: 6, color: z.stav === 'aktivni' ? 'var(--text-secondary)' : mutedColor }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: z.stav === 'aktivni' ? OK : 'rgba(148,163,184,0.6)' }} />
            <span className="truncate">{STAV_LABEL[z.stav] || z.stav}</span>
        </span>
    );
}

const uvazekKod = (z) => (z.typ_uvazku?.kod && z.typ_uvazku.kod !== 'ZADNY' ? z.typ_uvazku.kod : null);

function RadekMrizka({ z, grid, sloupce, aktivni, onSelect, checklist, isDark, mutedColor, hranice }) {
    const bunka = (s) => {
        switch (s.id) {
            case 'jmeno':
                return (
                    <span className="flex items-center min-w-0" style={{ gap: 10 }}>
                        <Avatar z={z} size={30} />
                        <span className="min-w-0">
                            <span className="block truncate font-semibold" style={{ color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>{prijmeniJmeno(z)}</span>
                            {z.telefon && <span className="block truncate text-[11.5px]" style={{ color: mutedColor }}>{z.telefon}</span>}
                        </span>
                    </span>
                );
            case 'oddeleni': return <span className="truncate">{z.oddeleni?.map(o => o.nazev).join(', ') || <span style={{ color: mutedColor }}>—</span>}</span>;
            case 'uvazek': return <span>{uvazekKod(z) || <span style={{ color: mutedColor }}>—</span>}</span>;
            case 'stav': return <Stav z={z} mutedColor={mutedColor} />;
            case 'nadrizeny': return <span className="truncate">{z.nadrizeny ? prijmeniJmeno(z.nadrizeny) : <span style={{ color: mutedColor }}>—</span>}</span>;
            case 'nastup': return <span className="tabular-nums">{datum(z.datum_nastupu) || <span style={{ color: mutedColor }}>—</span>}</span>;
            case 'onboarding': return <OnboardingBunka z={z} checklist={checklist} mutedColor={mutedColor} isDark={isDark} />;
            default: return s.cert ? <CertBunka z={z} kod={s.cert} mutedColor={mutedColor} /> : null;
        }
    };
    return (
        <button type="button" onClick={() => onSelect(z.id)} data-list-id={z.id}
            className="w-full text-left"
            style={{
                ...grid, padding: '8px 14px', minHeight: 50, border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent',
                fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer',
            }}>
            {sloupce.map(s => <span key={s.id} className="min-w-0 truncate" style={{ display: 'block' }}>{bunka(s)}</span>)}
        </button>
    );
}

function RadekUzky({ z, aktivni, onSelect, certTypy, checklist, nepodepsano, isDark, mutedColor, hranice, kompaktni }) {
    const chybi = pozadovaneCertifikaty(z).filter(kod => ['chybi', 'expirovano'].includes(stavCertifikatu(z, kod).stav));
    const ob = onboarding(checklist);
    const meta = [
        z.pozice?.nazev,
        uvazekKod(z),
        ob && vNastupu(z) ? `nástup ${ob.hotovo}/${ob.celkem}` : null,
    ].filter(Boolean).join(' · ');
    const problem = [
        ...chybi.map(kod => `chybí ${certTypy.find(t => t.kod === kod)?.nazev || kod}`),
        nepodepsano > 0 ? `${nepodepsano} nepodepsáno` : null,
    ].filter(Boolean).join(' · ');
    return (
        <button type="button" onClick={() => onSelect(z.id)} data-list-id={z.id}
            className="w-full text-left flex items-center"
            style={{
                gap: 12, padding: kompaktni ? '0 14px' : '0 12px', minHeight: kompaktni ? 60 : 54,
                border: 'none', borderBottom: `1px solid ${hranice}`,
                borderLeft: `3px solid ${aktivni ? SECTION_COLOR : 'transparent'}`,
                background: aktivni ? `${SECTION_COLOR}14` : 'transparent', cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
            }}>
            <Avatar z={z} size={kompaktni ? 38 : 32} />
            <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold" style={{ fontSize: kompaktni ? 15 : 13, color: aktivni ? SECTION_COLOR : 'var(--text-primary)' }}>
                    {prijmeniJmeno(z)}
                </span>
                <span className="block truncate" style={{ fontSize: kompaktni ? 12.5 : 11.5, color: mutedColor }}>
                    {meta}{problem && <>{meta ? ' · ' : ''}<span style={{ color: '#f87171', fontWeight: 600 }}>{problem}</span></>}
                </span>
            </span>
            <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: z.stav === 'aktivni' ? OK : 'rgba(148,163,184,0.6)' }}
                title={STAV_LABEL[z.stav]} />
        </button>
    );
}

/** Řádek skupiny (pozice) — souhrn ve stejných sloupcích jako lidé. */
function SkupinaRadek({ sk, sbalena, onClick, siroky, kompaktni, grid, sloupce, onboardingMap, isDark, mutedColor, hranice }) {
    const Sipka = sbalena ? ChevronRight : ChevronDown;
    const nastupuje = sk.lide.filter(vNastupu).length;
    const souhrnCert = (kod) => {
        const potrebuji = sk.lide.filter(z => pozadovaneCertifikaty(z).includes(kod));
        if (!potrebuji.length) return null;
        const ok = potrebuji.filter(z => ['ok', 'brzy'].includes(stavCertifikatu(z, kod).stav)).length;
        return <span className="tabular-nums" style={{ color: ok < potrebuji.length ? '#f87171' : undefined, fontWeight: ok < potrebuji.length ? 700 : 600 }}>{ok}/{potrebuji.length}</span>;
    };
    const nazev = (
        <span className="flex items-center min-w-0" style={{ gap: 8, color: 'var(--text-primary)' }}>
            <Sipka size={15} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
            <span className="truncate font-semibold">{sk.nazev}</span>
            <span style={{ color: mutedColor, fontWeight: 500 }}>{sk.lide.length}</span>
        </span>
    );
    const zaklad = {
        width: '100%', border: 'none', borderBottom: `1px solid ${hranice}`, cursor: 'pointer', textAlign: 'left',
        background: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)', fontSize: 12.5, color: mutedColor,
        WebkitTapHighlightColor: 'transparent',
    };
    if (!siroky) {
        return (
            <button type="button" onClick={onClick} className="flex items-center" style={{ ...zaklad, minHeight: kompaktni ? 44 : 34, padding: '0 12px', gap: 8 }}>
                {nazev}
                {nastupuje > 0 && <span style={{ marginLeft: 'auto' }}>{nastupuje} v nástupu</span>}
            </button>
        );
    }
    return (
        <button type="button" onClick={onClick} style={{ ...zaklad, ...grid, padding: '0 14px', minHeight: 40 }}>
            {sloupce.map(s => (
                <span key={s.id} className="min-w-0">
                    {s.id === 'jmeno' ? nazev
                        : s.id === 'stav' ? <span style={{ fontWeight: 600 }}>{sk.lide.filter(z => z.stav === 'aktivni').length} aktivní</span>
                        : s.id === 'onboarding' ? <span style={{ fontWeight: 600 }}>{nastupuje ? `${nastupuje} v nástupu` : 'hotovo'}</span>
                        : s.cert ? souhrnCert(s.cert) : null}
                </span>
            ))}
        </button>
    );
}
